import { createContext } from 'preact';
import { useCallback, useContext, useMemo } from 'preact/hooks';
import api from '@/api';
import { useReducerWithMiddleware } from '@/hooks';
import { getWebsiteDataValue, setCookie, debounce, rnd } from '@/utils';
import { alwaysNotVisibleBlocks } from '@/constants';
import { getInitialElementValue, getCurrentVisibleId, getNextVisibleId } from '../utils';
import { validate } from '../utils/validation';
import { applyLogic, applyStartingLogic } from '../utils/logic';
import { useFormProps } from './ConfigStore';
import { honeyBotId } from '@/constants';

const allowedEmails = [];
const deniedEmails = [];

function getNestedBlockProps({ value = '', showError = false, data_field }) {
	return {
		value,
		showError,
		data_field
	};
}

function getContactBlockProps({ block = {} }) {
	return Object.keys(block.properties?.elements ?? {}).reduce((fields, elementKey) => {
		const element = block.properties.elements[elementKey];

		//contact element has a show property which decides which elements are in the block
		if (!element.show) {
			return fields;
		}

		if (!element.combine && element.subelements) {
			Object.keys(element.subelements).forEach(elementKey => {
				const subElement = element.subelements[elementKey];
				fields[Number(subElement.data_field)] = getNestedBlockProps({
					showError: element.required,
					data_field: Number(subElement.data_field)
				});
			});
			return fields;
		}

		fields[Number(element.data_field)] = getNestedBlockProps({
			showError: element.required,
			data_field: Number(element.data_field)
		});

		return fields;
	}, {});
}

function getScreenshotBlockProps({ block = {} }) {
	return Object.keys(block.properties?.elements ?? {}).reduce((fields, elementKey) => {
		fields[elementKey] = getNestedBlockProps({});

		return fields;
	}, {});
}

function getWebsiteDataBlockProps({ block, divName, multipleIndex }) {
	return Object.keys(block.properties?.elements ?? {}).reduce((fields, elementKey) => {
		const element = block.properties.elements[elementKey];
		const value = getWebsiteDataValue({
			type: element.type,
			value: element.value,
			divName,
			multipleIndex
		});

		fields[Number(element.data_field)] = getNestedBlockProps({
			value,
			data_field: Number(element.data_field)
		});
		return fields;
	}, {});
}

function getDefaultSubProps({ block }) {
	return Object.keys(block.properties?.elements ?? {}).reduce((fields, elementKey) => {
		const element = block.properties.elements[elementKey];

		if (element.data_field) {
			fields[Number(element.data_field)] = getNestedBlockProps({
				data_field: Number(element.data_field)
			});
		}

		return fields;
	}, {});
}

function getMatrixBlockProps({ block }) {
	return Object.keys(block.properties?.elements ?? {}).reduce((fields, elementKey) => {
		const element = block.properties.elements[elementKey];
		//handles matrix elements
		if (typeof element.data_field === 'object') {
			Object.keys(element.data_field).forEach(subFieldKey => {
				const subFieldId = element.data_field[subFieldKey];
				fields[subFieldId] = getNestedBlockProps({
					data_field: subFieldId
				});
			});
		}
		return fields;
	}, {});
}

function getSubFieldBlockProps(props) {
	switch (props.block.typeName) {
		case 'screenshot':
			return getScreenshotBlockProps(props);
		case 'website_data':
			return getWebsiteDataBlockProps(props);
		case 'contact':
			return getContactBlockProps(props);
		case 'matrix':
			return getMatrixBlockProps(props);
		default:
			return getDefaultSubProps(props);
	}
}

function getElementExtraProps(elementsExtra) {
	return Object.keys(elementsExtra).reduce((fields, elementKey) => {
		const element = elementsExtra[elementKey];

		fields[element.data_field] = getNestedBlockProps({
			data_field: Number(element.data_field),
			isVisible: false
		});

		return fields;
	}, {});
}

function getBlockProps({
	block = {},
	initialValue = {},
	isFirst,
	divName,
	multipleIndex,
	numOfType
}) {
	const { post, value } = initialValue;

	const blockProps = {
		value,
		id: Number(block.id),
		data_field: Number(block.data_field),
		showError: (block.properties?.required && !initialValue.value) || false,
		focusInvalid: false,
		isVisible: !block.properties.hide_on_init,
		showInConvo: isFirst,
		typeCount: numOfType,
		autopost: post,
		...getSubFieldBlockProps({
			block,
			multipleIndex,
			divName
		}),
		...getElementExtraProps(block.properties?.elements_extra ?? {})
	};

	return blockProps;
}

function getInitialState({
	blocks = {},
	properties = {},
	layout = [],
	divName,
	multipleIndex,
	surveyRules,
	blockRules
}) {
	const typeCount = {};
	const state = {};

	const lastUpdatedBlocks = [];
	let initialPost = false;

	Object.keys(blocks).forEach(blockId => {
		const block = blocks[blockId];
		const numOfType = (typeCount[block.typeName] = (typeCount[block.typeName] ?? 0) + 1);
		const isFirst = layout.indexOf(Number(block.id)) === 0;

		const initialValue = getInitialElementValue(block);
		if (initialValue.post) {
			lastUpdatedBlocks.push(block.id);
			initialPost = initialValue.post;
		}
		state[blockId] = getBlockProps({
			block,
			initialValue,
			isFirst,
			numOfType,
			divName,
			multipleIndex
		});
		//circular reference only meant for lookup
		state[block.uuid] = state[blockId];
	});

	Object.keys(properties.thanksPage?.elements || {}).forEach(thanksPageElementKey => {
		const element = properties.thanksPage.elements[thanksPageElementKey];

		state[element.genID] = {
			isVisible: !element.hide_on_init
		};
	});

	if (properties.advanced?.recaptcha?.siteKey) {
		state.recaptcha = {
			id: 'recaptcha',
			value: '',
			isVisible: true,
			showError: true
		};
	}

	state[honeyBotId] = {
		id: honeyBotId,
		value: '',
		isVisible: true,
		showError: false,
		honey: true
	};

	const { newState: stateWithStartingLogic, updateFromLogic } = applyStartingLogic({
		state,
		layout,
		surveyRules
	});

	if (Object.keys(updateFromLogic).length > 0) {
		lastUpdatedBlocks.push(...Object.keys(updateFromLogic));
	}

	if (lastUpdatedBlocks.length > 0) {
		const newState = {
			...stateWithStartingLogic
		};
		let submitFromLogic = false;

		lastUpdatedBlocks.forEach(blockId => {
			const { submitFromLogic: submitFromBlockLogic, newState: newStateFromLogic } =
				applyLogic({
					state: newState,
					block: blocks[blockId],
					rules: blockRules[blockId],
					layout
				});

			if (submitFromBlockLogic) {
				submitFromLogic = submitFromBlockLogic;
			}

			Object.assign(newState, newStateFromLogic);
		});

		return {
			...newState,
			submitFromLogic,
			lastUpdatedBlock: lastUpdatedBlocks.pop(),
			initialPost
		};
	}

	return {
		...stateWithStartingLogic,
		lastUpdatedBlock: null,
		initialPost: false
	};
}

function showExtraField({ state, extraInputElement, extraQuestionElement, block }) {
	if (block.typeName === 'checkbox') {
		return Boolean(state[extraQuestionElement.data_field].value);
	}

	return state.value === (extraInputElement.value ?? extraInputElement.label);
}

function getExtraInputElement(block = {}) {
	if (!block.properties?.elements_extra) {
		return null;
	}
	return block.properties.elements_extra[
		Object.keys(block.properties?.elements_extra).pop()
	];
}

function getExtraQuestionElement(block = {}) {
	if (!block.properties?.elements_extra) {
		return null;
	}
	return block.properties.elements_extra[
		Object.keys(block.properties.elements_extra).shift()
	];
}

function applyUpdate({
	state,
	value = '',
	skip,
	datafieldId,
	block = {},
	rules = [],
	layout,
	type
}) {
	const blockStateWithNewValue = !datafieldId
		? {
				...state[block.id],
				inputSet: true,
				wasPrefilled: false,
				value,
				...(skip && {
					isSkipped: true
				})
		  }
		: {
				...state[block.id],
				[datafieldId]: {
					...state[block.id][datafieldId],
					inputSet: true,
					wasPrefilled: false,
					value,
					...(skip && {
						isSkipped: true
					})
				}
		  };

	const extraQuestionElement = getExtraQuestionElement(block);
	const extraInputElement = getExtraInputElement(block);

	const newBlockState = extraInputElement
		? {
				...blockStateWithNewValue,
				[extraInputElement.data_field]: {
					...blockStateWithNewValue[extraInputElement.data_field],
					isVisible: showExtraField({
						state: blockStateWithNewValue,
						extraInputElement,
						extraQuestionElement,
						block
					})
				}
		  }
		: blockStateWithNewValue;

	const validated = validate({
		state: newBlockState,
		datafieldId,
		block,
		allowedEmails
	});

	const newState = {
		...state,
		[block.id]: {
			...newBlockState,
			...(block.typeName !== 'contact' && {
				showError: !validated.valid,
				errorReason: validated.reason
			}),
			...(block.typeName === 'contact' && {
				[datafieldId]: {
					...newBlockState[datafieldId],
					showError: !validated.valid,
					errorReason: validated.reason
				}
			})
		},
		lastUpdatedBlock: block.id
	};

	const { newState: newStateWithLogic, submitFromLogic } = applyLogic({
		state: newState,
		block,
		rules,
		layout
	});

	return {
		...newStateWithLogic,
		action: `${type}-${rnd()}`,
		submitFromLogic
	};
}

function applyShowNext({ state, blockRules, blocks, layout, type }) {
	//make sure the layout only contains showable blocks
	const showableLayout = layout.filter(blockId => {
		const block = blocks[blockId];
		return alwaysNotVisibleBlocks.indexOf(block.typeName) === -1;
	});

	const currentId = getCurrentVisibleId({
		layout: showableLayout,
		blockState: state,
		blocks
	});
	const rules = blockRules[currentId];

	const { newState: newStateWithLogic } = applyLogic({
		state,
		block: blocks[currentId],
		rules,
		layout: showableLayout
	});

	const nextId = getNextVisibleId({
		layout: showableLayout,
		blockState: newStateWithLogic,
		blocks
	});

	return {
		...newStateWithLogic,
		[nextId]: {
			...newStateWithLogic[nextId],
			showInConvo: true
		},
		action: `${type}-${rnd()}`
	};
}

function reducer(state, action) {
	switch (action.type) {
		case 'set_value':
			return applyUpdate({
				state,
				...action.payload,
				type: action.type
			});

		case 'set_nested_value':
			return applyUpdate({
				state,
				...action.payload,
				type: action.type
			});

		case 'set_honeybot_value':
			return {
				...state,
				[honeyBotId]: {
					...state[honeyBotId],
					value: action.payload.value
				},
				type: action.type
			};

		case 'validate_block':
			const validatedBlock = validate({
				state: state[action.payload.block.id],
				datafieldId: action.payload.datafieldId,
				block: action.payload.block,
				allowedEmails
			});

			return {
				...state,
				[action.payload.block.id]: {
					...state[action.payload.block.id],
					...(action.payload.block.typeName !== 'contact' && {
						showError: !validatedBlock.valid,
						errorReason: validatedBlock.reason
					}),
					...(action.payload.block.typeName === 'contact' && {
						[action.payload.datafieldId]: {
							...state[action.payload.block.id][action.payload.datafieldId],
							showError: !validatedBlock.valid,
							errorReason: validatedBlock.reason
						}
					})
				}
			};

		case 'set_business_email_loading':
			return {
				...state,
				[action.payload.block.id]: {
					...state[action.payload.block.id],
					...(action.payload.block.typeName !== 'contact' && {
						isLoading: action.payload.loading
					}),
					...(action.payload.block.typeName === 'contact' && {
						[action.payload.datafieldId]: {
							...state[action.payload.block.id][action.payload.datafieldId],
							isLoading: action.payload.loading
						}
					})
				}
			};

		case 'set_skip':
			return applyUpdate({
				state,
				...action.payload,
				skip: true,
				type: action.type
			});

		case 'set_show_next_convo':
			return applyShowNext({
				state,
				...action.payload,
				type: action.type
			});

		case 'reset_form':
			return {
				...action.payload,
				action: action.type
			};

		default:
			return state;
	}
}

function createSetValueCookie(formKey, cookieTime) {
	return action => {
		const { block = {}, value, datafieldId } = action?.payload ?? {};

		if (!block.properties?.setScoreCookie) {
			return;
		}

		if (block.typeName === 'checkbox') {
			Object.keys(block.properties?.elements ?? {}).forEach(elementKey => {
				const element = block.properties?.elements[elementKey];
				if (element.data_field === datafieldId) {
					setCookie(`MSanswer.${formKey}.${block.id}`, element.label, cookieTime);
				}
			});

			return;
		}

		setCookie(`MSanswer.${formKey}.${block.id}`, value, cookieTime);
	};
}

const checkBusinessEmail = debounce(function checkBusinessEmail({
	action,
	dispatch,
	domain,
	customDomain
}) {
	const { type, payload: { value, block = {}, datafieldId } = {} } = action;

	if (type !== 'set_value' && type !== 'set_nested_value') {
		return;
	}

	if (
		!block.properties?.business_only &&
		!block.properties?.elements?.email?.business_only
	) {
		return;
	}

	if (
		block.properties?.elements?.email?.business_only &&
		datafieldId !== block.properties?.elements?.email?.data_field
	) {
		return;
	}

	if (
		value.length < 5 ||
		value.indexOf('@') === -1 ||
		allowedEmails.indexOf(value) > -1 ||
		deniedEmails.indexOf(value) > -1
	) {
		return;
	}

	dispatch({
		type: 'set_business_email_loading',
		payload: {
			block,
			datafieldId,
			loading: true
		}
	});

	api({ domain, customDomain })
		.validateBusinessEmail({ email: value })
		.then(response => {
			dispatch({
				type: 'set_business_email_loading',
				payload: {
					block,
					datafieldId,
					loading: false
				}
			});
			if (response.code !== 200) {
				return;
			}

			if (response.allowMail) {
				allowedEmails.push(value);
			} else {
				deniedEmails.push(value);
			}

			dispatch({
				type: 'validate_block',
				payload: {
					value,
					block,
					datafieldId
				}
			});
		});
},
500);

export function useCreateBlockStore({
	blocks,
	properties,
	layout,
	blockRules,
	surveyRules,
	...props
}) {
	const { multipleIndex, divName, formKey, cookieTime, domain, customDomain, extra } =
		useFormProps();
	const setValueCookie = createSetValueCookie(formKey, cookieTime);
	const validateBusinessEmail = useCallback(
		(action, state, dispatch) => {
			return checkBusinessEmail({
				action,
				dispatch,
				domain,
				customDomain
			});
		},
		[domain, customDomain]
	);

	//only handles conversational forms, does not need to handle nested datafields
	const showNextBusinessEmail = useCallback(
		(action, state, dispatch) => {
			if (action.type !== 'validate_block') {
				return;
			}

			const { block } = action.payload;

			if (!block.properties?.business_only) {
				return;
			}

			const blockState = state[block.id];
			if (blockState.showError) {
				return;
			}

			dispatch({
				type: 'set_show_next_convo',
				payload: { blocks, layout, blockRules }
			});
		},
		[blocks, layout, blockRules]
	);

	const initialState = useMemo(() => {
		return getInitialState({
			blocks,
			layout,
			divName,
			multipleIndex,
			properties,
			surveyRules,
			blockRules
		});
	}, [blocks, layout, divName, multipleIndex, properties, surveyRules]);

	const [state, dispatch] = useReducerWithMiddleware(reducer, initialState, {
		middleware: [validateBusinessEmail],
		afterware: [showNextBusinessEmail, setValueCookie]
	});

	return {
		...state,
		actions: {
			dispatch,
			reset: () => {
				dispatch({
					type: 'reset_form',
					payload: initialState
				});
			}
		}
	};
}

const BlockStoreContext = createContext({});

function BlockStoreProvider({ children, initialConfig, ...props }) {
	const state = useCreateBlockStore({ ...initialConfig });
	return (
		<BlockStoreContext.Provider value={state}>{children}</BlockStoreContext.Provider>
	);
}

function useBlockStore() {
	return useContext(BlockStoreContext);
}

export { BlockStoreContext, BlockStoreProvider, useBlockStore };
